import { useState } from 'react';

import * as FileSaver from 'file-saver';

import { BodySmall, BodyXS, Box, FileSelector, FileSelectorFileType, Link, Modal, ModalHeader, OpenInNewIcon, Snackbar } from '@parspec/pixel';

import { useUploadMfgFile } from './queries';
import { ManufacturerUploadResponse } from './queries/apiTypes';
import { DEAFULT_SNACKBAR_MSG } from '../UserManagement/utils/constants';

interface ImportListDialogProps {
    open: boolean;
    handleDialog: () => void;
    confirmAction: (uploadedData: ManufacturerUploadResponse[]) => void;
}

const UNSUPPORTED_CHARACTERS_ERROR_MESSAGE = "Unsupported characters in Manufacturer Name or Abbreviations, supported characters are letters, numbers, and common special characters."

const UnsuppportedCharacterMessage = () => {
    return (
        <Box>
            <BodyXS color={'error'} limit={false}>
                {UNSUPPORTED_CHARACTERS_ERROR_MESSAGE}
                <Box component='span'>
                    <Link
                        href="https://knowledgebase.parspec.io/knowledge/manufacturers-abbreviations"
                        target="_blank"
                        rel="noopener"
                        sx={{
                            underline: 'none',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'primary'
                            },
                            display: 'inline-flex', alignItems: 'center', ml: 0.5
                        }}
                    >
                        <BodyXS limit={false} color={'primary.main'}>
                            Learn More
                        </BodyXS>
                        <OpenInNewIcon sx={{ fontSize: '14px', ml: 0.5, color: 'primary.main' }} />
                    </Link>
                </Box>
            </BodyXS>
        </Box>
    )
}


const ImportList = (props: ImportListDialogProps) => {
    const { open, handleDialog, confirmAction } = props;

    const { mutateAsync: uploadFile, isLoading } = useUploadMfgFile();

    const [fileError, setFileError] = useState<string | null | React.ReactNode>(null);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const downloadTemplate = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        const csv = 'Manufacturer Name (Required),Manufacturer Website,Manufacturer ERP Code,Abbreviation 1,Abbreviation 2,Abbreviation 3\n,,,\n,,,\n,,,\n,,,\n,,,\n';
        const contentType = 'text/csv';
        setSnackbarMessage('The download has started ! Check your Downloads folder');
        const csvFile = new Blob([csv], { type: contentType });
        FileSaver.saveAs(csvFile, 'Manufacturer_File_Template.csv');
    };

    const onUploadFile = async (files: File[] | FileSelectorFileType[]) => {
        if (files.length) {
            setFileError(null);
            const formData = new FormData();
            formData.append('file', files[0] as File);
            try {
                const uploadData = await uploadFile(formData);
                confirmAction(uploadData.data.data);
            } catch (err) {
                const errorType = (err as any).response?.data?.error_type
                if (errorType === 'unsupported_characters_error') {
                    setFileError(UnsuppportedCharacterMessage);
                } else {
                    setFileError((err as any).response?.data?.message || DEAFULT_SNACKBAR_MSG);
                }
            }
        }
    };

    const onDeleteFile = () => {
        setFileError(null);
    };

    return (
        <>
            <Snackbar open={Boolean(snackbarMessage)} onClose={() => setSnackbarMessage('')} message={snackbarMessage}></Snackbar>
            <Modal open={open} header={<ModalHeader title={'Upload Manufacturers'} onClose={handleDialog} />} footer={<></>}>
                <Box width={376} marginTop={4}>
                    <FileSelector
                        placeholder={
                            <BodySmall
                                sx={{
                                    div: {
                                        WebkitLineClamp: 'unset !important'
                                    }
                                }}
                            >
                                Download our <Link onClick={downloadTemplate}>Manufacturer File Template</Link>,<br />
                                then browse or drag your file to upload.
                            </BodySmall>
                        }
                        onSelect={onUploadFile}
                        acceptedFormats={['.xls', '.xlsx', '.csv']}
                        helperText={'Supported file types: XLS, XLSX, CSV'}
                        error={fileError!}
                        onDeleteFile={onDeleteFile}
                        isLoading={isLoading}
                    ></FileSelector>

                </Box>
            </Modal>
        </>
    );
};

export default ImportList;
